import { useEffect, useRef, useState } from "react";
import { getProjectAiaBillingSummary } from "../services/projectService";
import { AiaG702Summary, AiaG703LineItem, MoneyValue } from "./useProjectAiaSummary";

export interface AiaBillingMeta {
  projectId?: string;
  projectName?: string;
  projectType?: string;
  inspectionMode?: string;
  asOfDrawNumber?: number;
  generatedAt?: string;
}

export interface AiaG703GrandTotal {
  sovValue?: MoneyValue;
  previousBilled?: MoneyValue;
  currentApplication?: MoneyValue;
  currentStoredMaterial?: MoneyValue;
  totalCompletedAndStored?: MoneyValue;
  percentageComplete?: number;
  balanceToFinish?: MoneyValue;
}

export interface AiaDrawHistoryEntry {
  drawId: string;
  drawNumber: number;
  periodTo?: string;
  amountBilled?: MoneyValue;
  stored?: MoneyValue;
  cumulative?: MoneyValue;
  pctOfContract?: number;
  status?: string;
}

export interface AiaBillingSummaryData {
  meta: AiaBillingMeta | null;
  g702: AiaG702Summary | null;
  g703: {
    lineItems: AiaG703LineItem[];
    grandTotal?: AiaG703GrandTotal;
  };
  phaseFlags: unknown[];
  drawHistory: AiaDrawHistoryEntry[];
}

const EMPTY_STATE: AiaBillingSummaryData = {
  meta: null,
  g702: null,
  g703: { lineItems: [] },
  phaseFlags: [],
  drawHistory: [],
};

const getErrorMessage = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return "Failed to load AIA billing summary.";
};

export const useAiaBillingSummary = (
  projectId?: string,
  role: "admin" | "lender" | "owner" | "gc" = "admin",
  enabled = true,
) => {
  const [summary, setSummary] = useState<AiaBillingSummaryData>(EMPTY_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fetchedRef = useRef(false);

  // A new project should always get a fresh fetch the next time this is enabled.
  useEffect(() => {
    fetchedRef.current = false;
  }, [projectId, role]);

  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;

    let isActive = true;

    const loadAiaBillingSummary = async () => {
      if (!projectId) {
        if (!isActive) return;
        setSummary(EMPTY_STATE);
        setError("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const { data, error: requestError } = await getProjectAiaBillingSummary(
        projectId,
        role,
      );

      if (!isActive) return;

      if (requestError) {
        setSummary(EMPTY_STATE);
        setError(getErrorMessage(requestError));
        setLoading(false);
        return;
      }

      setSummary((data?.data as AiaBillingSummaryData) ?? EMPTY_STATE);
      setLoading(false);
    };

    loadAiaBillingSummary();

    return () => {
      isActive = false;
    };
  }, [projectId, role, enabled]);

  return { summary, loading, error };
};
